import type { ApplicationState, PersonalData } from './applicationReducer';

/**
 * Validation rules for the application form.
 *
 * Deliberately plain functions rather than the zod schemas in
 * `lib/form/schemas.ts`: those are built for TanStack forms, and these steps
 * hold their state in the widget's reducer.
 *
 * The rules return **message keys** from the `tutorApplication` namespace, not
 * copy — they are pure functions outside React and so cannot call
 * `useTranslations` themselves. Each step translates what it renders. Keeping
 * the keys here also means the rules can be unit-tested without an intl
 * provider, and a copy change never touches this file.
 */
export type FieldErrors<T extends string> = Partial<Record<T, MessageKey>>;

export type PersonalDataField = keyof PersonalData;

/** Every key these rules can return; all live in `tutorApplication`. */
export type MessageKey =
  | 'noAtSymbol'
  | 'salutationRequired'
  | 'firstnameRequired'
  | 'lastnameRequired'
  | 'streetRequired'
  | 'houseNumberRequired'
  | 'postalCodeRequired'
  | 'postalCodeInvalid'
  | 'cityRequired'
  | 'emailRequired'
  | 'emailInvalid'
  | 'phoneRequired'
  | 'phoneInvalid'
  | 'mobilePhoneInvalid'
  | 'locationRequired'
  | 'locationNotSelected'
  | 'degreeRequired'
  | 'subjectRequired'
  | 'levelRequired'
  | 'filesRequired'
  | 'consentRequired';

const PHONE_PATTERN = /^\+?\d{7,15}$/;
const stripPhoneNoise = (value: string) => value.replace(/[\s\-/()]/g, '');

/**
 * The no-`@` anti-spam rule (SH-278), mirrored from the backend DTOs so the
 * applicant sees it inline instead of a `400`. `email` is the one field it
 * must not apply to.
 */
const rejectsAtSymbol = (field: PersonalDataField): boolean =>
  field !== 'email';

export const validatePersonalDataField = (
  field: PersonalDataField,
  value: string,
): MessageKey | undefined => {
  const trimmed = value.trim();

  if (rejectsAtSymbol(field) && trimmed.includes('@')) {
    return 'noAtSymbol';
  }

  switch (field) {
    case 'salutation':
      return trimmed ? undefined : 'salutationRequired';
    case 'firstname':
      return trimmed ? undefined : 'firstnameRequired';
    case 'lastname':
      return trimmed ? undefined : 'lastnameRequired';
    case 'street':
      return trimmed ? undefined : 'streetRequired';
    case 'houseNumber':
      return trimmed ? undefined : 'houseNumberRequired';
    case 'postalCode':
      if (!trimmed) {
        return 'postalCodeRequired';
      }
      return /^\d{4,5}$/.test(trimmed) ? undefined : 'postalCodeInvalid';
    case 'city':
      return trimmed ? undefined : 'cityRequired';
    case 'email':
      if (!trimmed) {
        return 'emailRequired';
      }
      return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)
        ? undefined
        : 'emailInvalid';
    case 'phone':
      if (!trimmed) {
        return 'phoneRequired';
      }
      return PHONE_PATTERN.test(stripPhoneNoise(trimmed))
        ? undefined
        : 'phoneInvalid';
    case 'mobilePhone':
      // Optional — only validated once something has been typed.
      if (!trimmed) {
        return undefined;
      }
      return PHONE_PATTERN.test(stripPhoneNoise(trimmed))
        ? undefined
        : 'mobilePhoneInvalid';
    default:
      return undefined;
  }
};

export const validatePersonalData = (
  data: PersonalData,
): FieldErrors<PersonalDataField> => {
  const fields = Object.keys(data) as PersonalDataField[];
  return fields.reduce<FieldErrors<PersonalDataField>>((errors, field) => {
    const message = validatePersonalDataField(field, data[field]);
    if (message) {
      errors[field] = message;
    }
    return errors;
  }, {});
};

export interface LocationProblem {
  key: MessageKey;
  index: number;
}

/**
 * Step 2 — the desired Standort is required, the alternatives are not. But an
 * input holding text that never resolved to a Standort is an error in *every*
 * slot, required or not: the applicant typed a PLZ, saw no match, and would
 * otherwise advance with it silently discarded.
 *
 * Returns *every* offending slot, in slot order, so the step can mark all the
 * inputs the applicant has to fix rather than sending them back one at a time.
 * The two keys can therefore co-occur: an empty first slot is `locationRequired`
 * while a third slot holding junk is `locationNotSelected`.
 */
export const invalidLocations = (
  state: ApplicationState,
): LocationProblem[] => {
  const slugs = state.locationSlugs;
  const labels = state.locationLabels;
  const problems: LocationProblem[] = [];

  for (let index = 0; index < slugs.length; index++) {
    if (slugs[index]) {
      continue;
    }
    if ((labels[index] ?? '').trim() !== '') {
      problems.push({ key: 'locationNotSelected', index });
    } else if (index === 0) {
      // Only the desired Standort is mandatory; an untouched alternative is
      // not an error.
      problems.push({ key: 'locationRequired', index });
    }
  }

  return problems;
};

/** Message-only view of {@link invalidLocations}, reporting the first key. */
export const validateLocations = (
  state: ApplicationState,
): MessageKey | undefined => invalidLocations(state)[0]?.key;

/** Step 3 — one degree must be picked. */
export const validateDegree = (
  state: ApplicationState,
): MessageKey | undefined => (state.degree ? undefined : 'degreeRequired');

/**
 * Step 4 — the first subject group needs both a subject and a level.
 *
 * Returns a single key for the group rather than one per control: the design
 * shows one error box above the group, and naming the missing half is more
 * useful than two boxes.
 */
export const validateSubjects = (
  state: ApplicationState,
): MessageKey | undefined => {
  const first = state.subjects[0];
  if (!first?.subject) {
    return 'subjectRequired';
  }
  if (!first.level) {
    return 'levelRequired';
  }
  return undefined;
};

/** Step 6 — at least one attachment and the consent checkbox (AC 7). */
export const validateAttachments = (
  state: ApplicationState,
  consent: boolean,
): FieldErrors<'files' | 'consent'> => {
  const errors: FieldErrors<'files' | 'consent'> = {};
  if (!state.files.some((file) => file !== null)) {
    errors.files = 'filesRequired';
  }
  if (!consent) {
    errors.consent = 'consentRequired';
  }
  return errors;
};
